import { inject, Injectable } from '@angular/core';
import { addFont, PDF, PDFColor, PdfOptions } from '@grid-ui/pdf-generator';
import { asyncForEach } from '@maplecroft-typescript/utils';
import { BodyContentItem } from '../shared-models';
import { ImageResizerPipe } from '../shared-pipes';

@Injectable({
  providedIn: 'root',
})
export class PDFService {
  private readonly imageResizerPipe = inject(ImageResizerPipe);

  constructor() {
    addFont();
  }

  public newPDF(options: Partial<PdfOptions>): PDF {
    return new PDF(options);
  }

  public async addBodyItems(pdf: PDF, items: BodyContentItem[]): Promise<void> {
    let previousItemType: BodyContentItem['type'] | null = null;

    await asyncForEach(items, async ({ type, value }) => {
      const headingSubheadingSpacingTop = previousItemType && ['heading', 'subheading'].includes(previousItemType) ? 3 : 10;

      switch (type) {
        case 'paragraph': {
          // Handle paragraph blocks not being wrapped in <p> tags
          if (!value.match(/^<[^\s]+( [\w-]+=".+")?>/)) {
            pdf.addRichTextHTML(`<p>${value}</p>`);
          } else {
            pdf.addRichTextHTML(value);
          }
          break;
        }
        case 'focusbox': {
          pdf.addFocusBox(value);
          break;
        }
        case 'datawrapper': {
          const { title, subtitle, attribution, url, width, height } = value.image;
          await pdf.addBodyImage(this.imageResizerPipe.transform(url), {
            title,
            subtitle,
            source: `Source: ${attribution}`,
            width,
            height,
          });
          break;
        }
        case 'heading': {
          pdf.addText(value, {
            size: 18,
            weight: 'bold',
            color: PDFColor.Primary,
            spacingTop: headingSubheadingSpacingTop,
            preventSpanningMultiplePages: true,
            preventSpanningMultiplePagesAdditionalOffset: 20,
          });
          break;
        }
        case 'subheading': {
          pdf.addText(value, {
            size: 14,
            weight: 'bold',
            color: PDFColor.Secondary,
            spacingTop: headingSubheadingSpacingTop,
            preventSpanningMultiplePages: true,
            preventSpanningMultiplePagesAdditionalOffset: 20,
          });
          break;
        }
        case 'image': {
          const { title, subtitle, attribution, url, width, height } = value;
          await pdf.addBodyImage(this.imageResizerPipe.transform(url), {
            title,
            subtitle,
            source: `Source: ${attribution}`,
            width,
            height,
          });
          break;
        }
        case 'enhancedtable': {
          const { title, source, data } = value;
          const { first_row_is_table_header } = data;

          pdf.addTable(data.data, { title, source, firstRowIsHeader: first_row_is_table_header });
          break;
        }
      }

      previousItemType = type;
    });
  }
}
